import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useRegisterFinalStore, useRegisterVerifyStore } from '@lib/store';
import { Button, TextInput } from '@shared/widgets';
import { useRouteHook } from '@shared/hooks';

const checkPassword = (password: string = '') => {
  const result: Record<string, boolean> = {};
  result.length = password.length >= 8;
  result.number = !!password.match(/\d/gim);
  result.capital = !!password.match(/[A-Z]/g);
  result.symbol = !!password.match(/[\W\s_]/g);
  return result;
};

interface IForm {
  username: string;
  password: string;
  retypePassword: string;
  code: string;
}
const DEFAULT_VALUES: IForm = { username: '', password: '', retypePassword: '', code: '' };

interface StepTwoProps {
  className?: string;
}

export const StepTwo = (props: StepTwoProps) => {
  const registerVerifyStore = useRegisterVerifyStore();
  const registerFinalStore = useRegisterFinalStore();

  const { navigate } = useRouteHook();

  const { control, formState, handleSubmit, watch, setError } = useForm({ defaultValues: DEFAULT_VALUES });
  const password = watch('password');
  const retypePassword = watch('retypePassword');

  const { t } = useTranslation('register');

  useEffect(() => {
    if (password === retypePassword) setError('retypePassword', { message: '' });
  }, [password]);

  const submitHandler = async (data: IForm) => {
    const verifyResult = await registerVerifyStore.registerVerify({ code: data.code });
    if (verifyResult.error) {
      setError('code', { message: verifyResult.error });
      return;
    }
    const finalResult = await registerFinalStore.registerFinal({ username: data.username, password: data.password });
    if (finalResult.error) {
      setError('password', { message: finalResult.error });
      return;
    }
    if (finalResult.data) navigate('/');
  };

  const passwordHints = checkPassword(watch('password'));
  const isDisabled = !watch('username') || !watch('password') || !watch('retypePassword') || !watch('code') || formState.isSubmitting;
  return (
    <form className={clsx(props.className)} autoComplete='off' onSubmit={handleSubmit(submitHandler)}>
      <Controller
        name='username'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          minLength: { value: 6, message: t('stepTwo.errors.form.usernameFormat') },
          maxLength: { value: 30, message: t('stepTwo.errors.form.usernameFormat') },
          pattern: { value: /^\S+$/, message: t('stepTwo.errors.form.usernameFormat') },
        }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            captionClassName='!text-base !font-light'
            caption={`${t('placeholders.username', { ns: 'common' })}:`}
            name='username'
            required
            error={error?.message}
          />
        )}
      />
      <Controller
        name='password'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          pattern: {
            value: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?]).+$/,
            message: t('errors.form.password.invalidFormat', { ns: 'common' }),
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            captionClassName='!text-base !font-light'
            caption={`${t('placeholders.password', { ns: 'common' })}:`}
            name='password'
            type='password'
            required
            error={error?.message}
          />
        )}
      />
      <div className={'text-[#dadada] mb-5'}>
        {t('stepTwo.passwordHints.title')}
        <ul className={'mt-[0.1em] mb-[0.1em]'}>
          <li className={clsx('list-[circle]', passwordHints.length && 'list-[disc]')}>{t('stepTwo.passwordHints.minLength')}</li>
          <li className={clsx('list-[circle]', passwordHints.number && 'list-[disc]')}>{t('stepTwo.passwordHints.number')}</li>
          <li className={clsx('list-[circle]', passwordHints.capital && 'list-[disc]')}>{t('stepTwo.passwordHints.capital')}</li>
          <li className={clsx('list-[circle]', passwordHints.symbol && 'list-[disc]')}>{t('stepTwo.passwordHints.symbol')}</li>
        </ul>
      </div>
      <Controller
        name='retypePassword'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          validate: (value) => value === password || t('stepTwo.errors.form.passwordsMustEqual'),
        }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            captionClassName='!text-base !font-light'
            caption={t('stepTwo.placeholders.retypePassword')}
            name='retypePassword'
            type='password'
            required
            error={error?.message}
          />
        )}
      />
      <Controller
        name='code'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          minLength: { value: 6, message: t('stepTwo.errors.form.codeLength') },
          maxLength: { value: 6, message: t('stepTwo.errors.form.codeLength') },
        }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            captionClassName='!text-base !font-light'
            caption={`${t('placeholders.confirmationCode', { ns: 'common' })}:`}
            name='code'
            maxLength={6}
            inputPattern='\d'
            error={error?.message}
          />
        )}
      />
      <Button className='text-base h-[50px] mt-12.5' fullWidth caption={t('buttons.next', { ns: 'common' })} type='submit' disabled={isDisabled} />
    </form>
  );
};
